/**
 * The Dashboard and Revenue view (TMMPDM-0035): `period` and `reseller` in the URL, and the scope
 * and freshness lines beside the heading (DASH-005, RPT-001, PERF-004) —
 * "This month (1 Sep 2026 – 27 Sep 2026) · All resellers" and "Loaded at 10:42". Neither report
 * publishes an as-of time, so the freshness line is the client clock when the data arrived.
 */
import { oneOf, positiveInt, type UrlSchema } from '@/hooks/useUrlState';
import { formatDate, formatTime } from '@/format';
import type { Period, PeriodKey } from './period';

type Translate = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

export const PERIOD_KEYS: PeriodKey[] = ['THIS_MONTH', 'LAST_30_DAYS', 'LAST_MONTH'];

export type PeriodView = { period: PeriodKey; reseller: number | undefined };

export const PERIOD_VIEW: UrlSchema<PeriodView> = {
  period: { default: 'THIS_MONTH', parse: oneOf(PERIOD_KEYS) },
  reseller: { default: undefined, parse: positiveInt },
};

export function periodScope(
  t: Translate,
  locale: string,
  key: PeriodKey,
  period: Period,
  resellerText: string,
): string {
  return t('common.scope.period', '{{period}} ({{from}} – {{to}}) · {{reseller}}', {
    period: t(`dashboard.period.${key}`, key),
    from: formatDate(period.from, locale),
    to: formatDate(period.to, locale),
    reseller: resellerText,
  });
}

export function loadedAtLabel(t: Translate, locale: string, loadedAt: Date): string {
  return t('common.loadedAt', 'Loaded at {{time}}', {
    time: formatTime(loadedAt, locale),
  });
}
